/**
 * PartRequirementEditor — authors a STRUCTURED question's `requirement`
 * (Phase 1C choice / K-of-N). Self-contained: owns the mode + Custom-expression
 * UI state and emits a validated `PartRequirement` (or `undefined` for "All
 * parts") to the parent via `onChange`.
 *
 * Three modes:
 *   - All parts        → undefined (implicit `all`)
 *   - Any N of all     → any{ all parts, choose:N }
 *   - Custom           → free expression, e.g. "(a AND b) OR (c AND d)",
 *                        parsed + live-validated against the part ids.
 *
 * See docs/FeatureDesigns/question-structure.md §Phase 1C.
 */
import { useState, useEffect } from 'react';
import type { QuestionPart, PartRequirement } from '@padcanvas/shared';
import {
    anyOfParts,
    parseRequirementExpression,
    requirementToExpression,
} from '@padcanvas/shared';
import './PartRequirementEditor.css';
import { IntegerInput } from '../../components/IntegerInput';

type Mode = 'all' | 'anyN' | 'custom';

export interface PartRequirementEditorProps {
    parts: ReadonlyArray<QuestionPart>;
    requirement: PartRequirement | undefined;
    onChange: (requirement: PartRequirement | undefined) => void;
}

/** Is this requirement exactly "any N of ALL parts" (the preset shape)? */
function isAnyOfAllParts(req: PartRequirement, partIds: string[]): boolean {
    if (req.kind !== 'any') return false;
    if (req.of.length !== partIds.length) return false;
    if (!req.of.every(c => c.kind === 'part')) return false;
    const ids = new Set(req.of.map(c => (c as { partId: string }).partId));
    return partIds.every(id => ids.has(id));
}

/** Classify the stored requirement into the editor's initial mode. */
function initialModeFor(req: PartRequirement | undefined, partIds: string[]): Mode {
    if (!req) return 'all';
    if (isAnyOfAllParts(req, partIds)) return 'anyN';
    return 'custom';
}

export function PartRequirementEditor({ parts, requirement, onChange }: PartRequirementEditorProps) {
    const partIds = parts.map(p => p.partId);

    const [mode, setMode] = useState<Mode>(() => initialModeFor(requirement, partIds));
    const [n, setN] = useState<number>(() =>
        requirement?.kind === 'any' ? requirement.choose : 1);
    const [customText, setCustomText] = useState<string>(() =>
        requirement ? requirementToExpression(requirement) : '');
    const [customError, setCustomError] = useState<string | null>(null);

    // Keep the Any-N preset in sync as parts are added/removed (the preset
    // always spans every current part). Re-emit so a part removed/added is
    // reflected in the stored requirement without re-touching the control.
    useEffect(() => {
        if (mode !== 'anyN') return;
        if (partIds.length === 0) { onChange(undefined); return; }
        const choose = Math.min(Math.max(1, n), partIds.length);
        onChange(anyOfParts(partIds, choose));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, n, partIds.join(',')]);

    const selectMode = (next: Mode) => {
        setMode(next);
        if (next === 'all') {
            onChange(undefined);
        } else if (next === 'anyN') {
            // handled by the effect above
        } else {
            // entering Custom: seed from the current requirement and validate.
            const seed = requirement ? requirementToExpression(requirement) : '';
            setCustomText(seed);
            applyCustom(seed);
        }
    };

    const applyCustom = (text: string) => {
        setCustomText(text);
        if (!text.trim()) {
            // empty Custom ⇒ no constraint (same as All)
            setCustomError(null);
            onChange(undefined);
            return;
        }
        const result = parseRequirementExpression(text, partIds);
        if (result.ok) {
            setCustomError(null);
            onChange(result.requirement);
        } else {
            setCustomError(result.error);
            // leave the stored requirement unchanged until it parses — the
            // backend also re-validates on save as a safety net.
        }
    };

    if (parts.length < 2) {
        // A requirement is only meaningful with ≥2 parts.
        return null;
    }

    const previewText = (() => {
        if (mode === 'all') return null;
        if (mode === 'anyN') {
            const choose = Math.min(Math.max(1, n), partIds.length);
            return requirementToExpression(anyOfParts(partIds, choose));
        }
        if (customError) return null;
        const r = parseRequirementExpression(customText, partIds);
        return r.ok ? requirementToExpression(r.requirement) : null;
    })();

    return (
        <div className="req-editor">
            <label className="req-editor-title">Answer requirement</label>
            <p className="req-editor-help">
                How the parts combine. Default: the student must answer <strong>all</strong> parts.
            </p>

            <div className="req-editor-modes">
                <label className="req-editor-mode">
                    <input type="radio" name="req-mode" checked={mode === 'all'} onChange={() => selectMode('all')} />
                    <span>All parts</span>
                </label>
                <label className="req-editor-mode">
                    <input type="radio" name="req-mode" checked={mode === 'anyN'} onChange={() => selectMode('anyN')} />
                    <span>
                        Any{' '}
                        <IntegerInput
                            className="req-editor-n"
                            min={1}
                            max={partIds.length}
                            value={n}
                            disabled={mode !== 'anyN'}
                            onChange={v => { if (v != null) setN(v); }}
                        />{' '}
                        of {partIds.length} parts
                    </span>
                </label>
                <label className="req-editor-mode">
                    <input type="radio" name="req-mode" checked={mode === 'custom'} onChange={() => selectMode('custom')} />
                    <span>Custom</span>
                </label>
            </div>

            {mode === 'custom' && (
                <div className="req-editor-custom">
                    <input
                        type="text"
                        className={`req-editor-expr ${customError ? 'req-editor-expr--error' : ''}`}
                        value={customText}
                        onChange={e => applyCustom(e.target.value)}
                        placeholder="(a AND b) OR (c AND d)"
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                    />
                    <p className="req-editor-grammar">
                        Use part ids ({partIds.join(', ')}), <code>AND</code>, <code>OR</code>, parentheses,
                        and <code>ANY k OF (a, b, …)</code> for K-of-N. Each part must appear once.
                    </p>
                    {customError && <p className="req-editor-error">⚠ {customError}</p>}
                </div>
            )}

            {previewText && (
                <p className="req-editor-preview">
                    <span className="req-editor-preview-label">Requirement:</span> <code>{previewText}</code>
                </p>
            )}
        </div>
    );
}
